import Axe from "axe-core";
import reactAxe from "@axe-core/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import WidgetPanel from "./WidgetPanel";
import useStyles from "./AccessibilityPanel.styles";
import { Collapsible } from "../../shared/Collapsible";

interface AccessibilityIssueProps {
    label: string;
    severity: Axe.ImpactValue;
    details: string;
    nodes: Axe.NodeResult[];
}

function AccessibilityIssue({ label, severity, details, nodes }: AccessibilityIssueProps) {
    const [showDetails, setShowDetails] = React.useState(false);
    const classes = useStyles(severity);
    const { t } = useTranslation();

    const severityLabel =
        severity === "critical"
            ? t("debug.panels.accessibility.label.error")
            : t("debug.panels.accessibility.label.warning");

    return (
        <div className={classes.issueContainer}>
            <div className={classes.issueValue}>
                <span className={classes.issueNodeCount}>{nodes.length}</span>
                <span className={classes.issueLabel}>{label}</span>
                <span className={classes.issueSeverity}>{severityLabel}</span>
                <div
                    role="checkbox"
                    aria-checked={showDetails}
                    className={classes.issueDetailsIcon}
                    tabIndex={0}
                    onKeyPress={(event) => {
                        if (event.key === " ") {
                            setShowDetails((toggleShowDetails) => !toggleShowDetails);
                            event.preventDefault();
                        }
                    }}
                    onClick={() => setShowDetails((toggleShowDetails) => !toggleShowDetails)}
                >
                    <FontAwesomeIcon
                        fixedWidth
                        icon={["far", showDetails ? "chevron-up" : "chevron-down"]}
                    />
                </div>
            </div>
            <Collapsible expanded={showDetails}>
                <div className={classes.issueDetails}>
                    <span>{details}</span>
                    <div>
                        <div className={classes.issueElements}>
                            {t("debug.panels.accessibility.label.elements")}
                        </div>
                        {nodes.map((node) => (
                            <textarea
                                key={node.html}
                                className={classes.issueElementHtml}
                                rows={4}
                                value={node.html}
                                readOnly
                                onFocus={() => {
                                    if (node.target.length === 1) {
                                        const [target] = node.target;
                                        const element = document.querySelector(target);
                                        if (element) {
                                            element.scrollIntoView();
                                        }
                                    }
                                }}
                            />
                        ))}
                    </div>
                </div>
            </Collapsible>
        </div>
    );
}

interface State {
    violations: Axe.Result[];
}

export class AccessibilityPanel extends React.Component<{ title?: string }, State> {
    ref: React.Ref<HTMLDivElement>;

    constructor(props: {}) {
        super(props);
        this.state = {
            violations: [],
        };

        this.ref = React.createRef<HTMLDivElement>();
    }

    componentDidMount(): void {
        const context = {
            include: [[".survey-engine"]],
            resultTypes: ["violations"],
        };

        const options = {
            runOnly: ["wcag2a", "wcag2aa"],
            disableDeduplicate: true,
        };

        reactAxe(React, ReactDOM, 1000, options, context, (results) => {
            this.setState({
                violations: results.violations.sort((a, b) => a.impact.localeCompare(b.impact)),
            });
        });
    }

    componentWillUnmount(): void {
        Axe.cleanup();
    }

    render(): React.ReactNode {
        const { violations } = this.state;
        const { title } = this.props;
        return (
            <WidgetPanel title={title} containerRef={this.ref}>
                {violations.map((violation) => (
                    <AccessibilityIssue
                        key={violation.id}
                        label={violation.help}
                        severity={violation.impact}
                        details={violation.description}
                        nodes={violation.nodes}
                    />
                ))}
            </WidgetPanel>
        );
    }
}
